import {PaletteMode} from '@mui/material';
import {alpha, createTheme} from '@mui/material/styles';

export const getDesignTokens = (mode: PaletteMode): Parameters<typeof createTheme>[0] => ({
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    htmlFontSize: 14, // Définit 1rem = 14px
    fontSize: 14, // Taille par défaut pour les composants
    body1: {
      fontSize: '1rem', // 14px
    },
    body2: {
      fontSize: '0.875rem', // 12.25px
    },
    h1: {
      fontSize: '2rem', // 28px
    },
    h2: {
      fontSize: '1.5rem', // 21px
    },
  },
  palette: {
    mode,
    background: {
      default: mode === 'light' ? '#F5F6FA' : '#000',
      paper: mode === 'light' ? '#FFFFFF' : '#1b1f23',
      heading: mode === 'light' ? '#ECEEF6' : '#23263A',
    },
    primary: {
      main: '#0096FF',
    },
    secondary: {
      main: '#8E96AB',
    },
    success: {
      main: '#D0FCBC',
      // Texte posé SUR `main` (bouton contained, badge) : vert foncé dans les deux modes.
      // En sombre, '#D0FCBC' rendait le libellé invisible sur son propre fond.
      contrastText: '#3A7B1C',
    },
    error: {
      main: '#FF5F5F',
    },
    warning: {
      main: '#E4700B',
    },
    alert: {
      errorBg: mode === 'light' ? '#FCE7E4' : alpha('#FF5F5F', 0.15),
      warningBg: mode === 'light' ? '#FFEBCC' : alpha('#E4700B', 0.15),
      successBg: mode === 'light' ? '#D0FCBC' : alpha('#3A7B1C', 0.15),
    },
    chip: {
      defaultBg: mode === 'light' ? '#E2E5F0' : alpha('#FFFFFF', 0.08),
      defaultText: mode === 'light' ? '#707787' : alpha('#FFFFFF', 0.7),
      errorBg: mode === 'light' ? '#FCE7E4' : alpha('#FF5F5F', 0.15),
      warningBg: mode === 'light' ? '#FFD38D73' : alpha('#E4700B', 0.25),
      successText: mode === 'light' ? '#3A7B1C' : '#D0FCBC',
      successBg: mode === 'light' ? '#D0FCBC' : alpha('#3A7B1C', 0.15),
    },
    text: {
      primary: mode === 'light' ? '#183153' : '#FFFFFF',
      secondary: '#8E96AB',
    },
    divider: mode === 'light' ? '#E0E0E0' : alpha('#FFFFFF', 0.12),
  },
  components: {
    // Couleurs de la liste de suggestions de mentions (quill-mention), appliquées par
    // `.ql-mention-list-*` dans `index.css`. Le clair garde le rendu historique de la
    // bibliothèque (bordure #F0F0F0, élément sélectionné #D3E1EB).
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        ':root': {
          // Barre de défilement claire ou sombre selon le thème (la liste en a toujours une).
          '--pp-mention-list-color-scheme': theme.palette.mode,
          '--pp-mention-list-bg': theme.palette.background.paper,
          '--pp-mention-list-border': theme.palette.mode === 'light' ? '#F0F0F0' : theme.palette.divider,
          '--pp-mention-list-text': theme.palette.text.primary,
          '--pp-mention-list-text-secondary': theme.palette.text.secondary,
          '--pp-mention-list-hover': theme.palette.action.hover,
          '--pp-mention-list-selected': theme.palette.mode === 'light' ? '#D3E1EB' : theme.palette.action.selected,
        },
      }),
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ownerState, theme}) => {
          const {severity} = ownerState;
          return {
            borderRadius: 0,
            // La taille du corps de texte. À 10 px en gras, le message d'un encadré était plus
            // petit que le texte qui l'entoure, et son `AlertTitle` plus grand que lui.
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
            fontWeight: 500,
            // Le padding MUI par défaut (6px 16px) : à 0, l'icône collait aux bords et
            // l'action, qui a une marge droite négative chez MUI, débordait à droite.
            padding: theme.spacing(0.75, 2),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            ...(severity === 'error' && {
              backgroundColor: theme.palette.alert.errorBg,
              color: theme.palette.error.main,
            }),
            ...(severity === 'warning' && {
              backgroundColor: theme.palette.alert.warningBg,
              color: theme.palette.warning.main,
            }),
            ...(severity === 'success' && {
              backgroundColor: theme.palette.alert.successBg,
              color: theme.palette.chip.successText,
            }),
          };
        },
        message: {
          padding: 0,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiInput-root .MuiInput-input': {
            padding: '32px 16px 8px 0',
          },
          '& .MuiInputAdornment-root': {
            marginLeft: '16px',
            marginTop: '24px',
            marginRight: '0',
          },
        },
        tag: {
          maxWidth: '100%',
          margin: '0 5px 0 0',
        },
        popper: {
          zIndex: 1301,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          textTransform: 'none',
          letterSpacing: '0.3px',
          fontSize: 14,
          lineHeight: '16.94px',
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundImage: 'unset',
          borderRadius: '8px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({theme}) => ({
          padding: '0',
          color: theme.palette.secondary.main,
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: '5px 10px',
          fontWeight: 700,
          fontSize: 10,
          lineHeight: '12.1px',
          width: 'fit-content',
          height: 'fit-content',
        },
        avatar: {
          marginLeft: -10,
          marginRight: 5,
        },
        label: {
          padding: 0,
        },
        icon: {
          margin: 0,
          marginRight: 5,
        },
        colorDefault: ({theme}) => ({
          backgroundColor: theme.palette.chip.defaultBg,
          color: theme.palette.chip.defaultText,
        }),
        colorError: ({theme}) => ({
          backgroundColor: theme.palette.chip.errorBg,
          color: theme.palette.error.main,
        }),
        colorSuccess: ({theme}) => ({
          backgroundColor: theme.palette.chip.successBg,
          color: theme.palette.chip.successText,
        }),
        colorWarning: ({theme}) => ({
          backgroundColor: theme.palette.chip.warningBg,
          color: theme.palette.warning.main,
        }),
        deleteIcon: {
          margin: '0 0 0 5px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({theme}) => ({
          borderRadius: 8,
          border: `solid 1px ${theme.palette.primary.main}`,
        }),
        paperFullScreen: {
          border: 'none',
          borderRadius: 0,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: '0',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({theme}) => ({
          fontSize: '12px',
          color: theme.palette.secondary.main,
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: ({theme}) => ({
          color: theme.palette.error.main, // Rouge pour l'étoile
          fontWeight: 700,
        }),
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          marginTop: '0px !important', // Supprime la marge appliquée
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&::before': {
            borderBottom: 'none !important',
          },
          '&::after': {
            borderBottom: 'none !important',
          },
        },
        input: ({theme}) => ({
          padding: '32px 16px 8px', // Ajuste le padding interne pour ne pas chevaucher le label
          fontSize: '14px',
          lineHeight: '20px',
          color: alpha(theme.palette.text.primary, 0.7),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({theme}) => ({
          position: 'absolute',
          top: '8px', // Assure que le label est au-dessus de l'input
          left: '16px',
          fontSize: '12px',
          lineHeight: '15px',
          zIndex: 2,
          color: alpha(theme.palette.text.primary, 0.5),
          transform: 'none', // Désactive la translation quand l'input est rempli
          pointerEvents: 'none', // Évite les interactions inutiles avec le label
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
        }),
        shrink: {
          transform: 'none', // Désactive la translation quand l'input est rempli
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: ({theme}) => ({
          borderBottom: `solid 1px ${theme.palette.divider}`,
          paddingTop: 0,
          paddingBottom: 0,
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '14px',
          padding: '11px 19px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({theme}) => ({
          borderRadius: '8px',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
          },
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({theme}) => ({
          boxShadow: 'unset',
          borderRadius: 4,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: theme.palette.info.main,
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({theme}) => ({
          padding: '0',
          color: theme.palette.secondary.main,
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: 14,
          lineHeight: '20px',
          fontWeight: 600,
          padding: '5px 15px',
        },
        outlined: ({theme}) => ({
          borderWidth: '1px',
          color: theme.palette.secondary.main,
        }),
        iconOutlined: ({theme}) => ({
          color: theme.palette.secondary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({theme}) => ({
          fontSize: 24,
          fontWeight: 700,
          textTransform: 'unset',
          '&.Mui-selected': {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          '& .MuiTablePagination-spacer': {
            display: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({theme}) => ({
          borderRadius: '8px',
          '& .MuiInput-root': {
            backgroundColor: theme.palette.background.default,
            border: 'none', // Ajoute une bordure pour standard
            color: alpha(theme.palette.text.primary, 0.7),
          },
          // Champ `small` avec libellé : MUI réduit le padding de l'input, et le libellé, placé
          // DANS le champ par ce thème, chevauchait la valeur saisie. L'encoche que MUI découpe
          // dans le contour pour un libellé flottant n'a pas lieu d'être non plus.
          '&:has(> .MuiInputLabel-root) .MuiInputBase-inputSizeSmall': {
            paddingTop: '26px',
            paddingBottom: '6px',
          },
          '&:has(> .MuiInputLabel-root) .MuiInputBase-sizeSmall .MuiOutlinedInput-notchedOutline legend': {
            maxWidth: 0,
          },
        }),
      },
    },
  },
});

export const buildTheme = (mode: PaletteMode) => createTheme(getDesignTokens(mode));
